import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import { Alert, Button } from "@mui/material";
import { useFormikContext } from "formik";
import { Inventory } from "types/inventory";

const batteryHealthThreshold = 78;
const batteryCycleCountThreshold = 700;
const macBatteryHealthMargin = 3;
const macBatteryCycleMargin = 5;
const working = "Working";
const broken = "Broken";
const asIs = "As-is";

const withinRange = (value: number, target: number, range: number) => {
  return Math.abs(value - target) <= range;
};

const getColor = (status: string) => {
  switch (status) {
    case working:
      return "info";
    case asIs:
      return "warning";
    default:
      return "error";
  }
};

export default function SuggestedBatteryStatus() {
  const { values, setFieldValue } = useFormikContext<{ inventory: Inventory }>();
  const { batteryHealth, batteryCount } = values.inventory;
  const isApple = values.inventory.manufacturer === "Apple";
  const isLaptop = values.inventory.deviceType === "Laptop";
  const isPoorCondition = values.inventory.condition === "D";
  const batteryHealthValid = batteryHealth !== undefined && batteryHealth > 0;
  const batteryCountValid = batteryCount !== undefined && batteryCount >= 0;
  const isRepairedOrReplaced =
    values.inventory.batteryFunctionality === "Repaired" || values.inventory.batteryFunctionality === "Replaced";

  if (!isLaptop || !batteryHealthValid || !batteryCountValid || isRepairedOrReplaced) return null;

  const suggestedBatteryStatus = (() => {
    const healthy = batteryHealth >= batteryHealthThreshold;
    const lowCycles = batteryCount <= batteryCycleCountThreshold;

    if (isApple) {
      return healthy && lowCycles ? working : isPoorCondition ? asIs : broken;
    }
    return healthy ? working : asIs;
  })();

  const batteryHealthIsNearThreshold = (() => {
    const threshold = isPoorCondition ? batteryHealthThreshold : batteryHealthThreshold;

    if (isApple) {
      return withinRange(batteryHealth, threshold, macBatteryHealthMargin);
    }
    return withinRange(batteryHealth, batteryHealthThreshold, macBatteryHealthMargin);
  })();

  const batteryCountIsNearThreshold = (() => {
    const threshold = isPoorCondition ? batteryCycleCountThreshold : batteryCycleCountThreshold;

    if (isApple) {
      return withinRange(batteryCount, threshold, macBatteryCycleMargin);
    }
    return false;
  })();

  const color = getColor(suggestedBatteryStatus);

  return (
    <Alert
      severity={color}
      variant="outlined"
      icon={<TipsAndUpdatesIcon />}
      sx={{ width: "100%" }}
      action={
        values.inventory.batteryFunctionality !== suggestedBatteryStatus && (
          <Button
            tabIndex={-1}
            color={color}
            size="small"
            onClick={() => setFieldValue("inventory.batteryFunctionality", suggestedBatteryStatus)}
            variant="contained"
            sx={{ alignSelf: "center" }}
          >
            Apply now
          </Button>
        )
      }
    >
      Battery functionality should be <b>{suggestedBatteryStatus}</b>
      {(batteryCountIsNearThreshold || batteryHealthIsNearThreshold) && (
        <span>
          <br />
          Ensure that battery health and cycle count are accurate.
        </span>
      )}
    </Alert>
  );
}
